import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { ControlValueAccessor, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { OptionService } from '@app/services/option.service';
import { HypoControlsBaseComponent } from '@app/components/form-components/hypo-control-base/hypo-control-base.component';
import { OptionInterface } from '@app/interfaces/option.interface';
import { Subscription } from 'rxjs';
import { MAX_OPTIONS_PER_SELECT } from '@app/settings/settings.const';
import { debounceTime, first, takeUntil } from 'rxjs/operators';
import { FlexModule } from '@angular/flex-layout';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatFormField } from '@angular/material/form-field';
import { MatError } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { TooltipComponent } from '@app/components/tooltip/tooltip.component';

@Component({
    selector: 'app-hypo-select',
    templateUrl: './hypo-select.component.html',
    styleUrls: ['./hypo-select.component.scss'],
    imports: [
        ReactiveFormsModule,
        FlexModule,
        MatFormField,
        MatSelect,
        MatOption,
        MatError,
        NgxMatSelectSearchModule,
        TooltipComponent,
    ],
})
export class HypoSelectComponent extends HypoControlsBaseComponent implements ControlValueAccessor, OnInit, OnDestroy {
    private readonly optionService = inject(OptionService);

    @Input() label: string = '';
    @Input() listName: string = '';
    @Input() searchAble: boolean = true;
    @Input() dontPreload: boolean = false;
    @Input() valueVisibleInLabel: boolean = false;
    @Input() originalList: OptionInterface[] = [];
    showingList: OptionInterface[] = [];

    public searchControl: UntypedFormControl = new UntypedFormControl();

    private valueChangesSubscription!: Subscription;

    constructor() {
        super();
    }

    public ngOnInit() {
        super.ngOnInit();
        this.afterInit();
        this.bindValueChanges();
    }

    public filterCountryList(searchValue: string) {
        if (searchValue?.length) {
            this.showingList = this.originalList.filter((option) =>
                option.label.toLowerCase().includes(searchValue?.toLowerCase())
            );
        } else {
            this.resetShowingList();
        }
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
        this.unsubscribe();
    }

    public trackByValue(index: number, item: OptionInterface): any {
        return item.value;
    }

    private afterInit(): void {
        if (this.listName) {
            this.optionService.itemsLoadedObservable
                .pipe(
                    takeUntil(this.destroyed$),
                    first((loaded) => loaded)
                )
                .subscribe(() => {
                    this.originalList = this.optionService.getListByType(this.listName);
                    this.resetShowingList();
                });
        } else {
            if (this.originalList?.length) {
                this.showingList = [...this.originalList];
            }
        }
    }

    private resetShowingList(): void {
        if (!this.dontPreload) {
            this.showingList =
                this.originalList?.length < MAX_OPTIONS_PER_SELECT
                    ? this.originalList.slice()
                    : this.originalList.slice(0, MAX_OPTIONS_PER_SELECT);
        } else {
            this.showingList = [];
            if (this.input.value) {
                this.showingList.push(this.originalList.find((a) => a.value === this.input.value));
            }
        }
    }

    private bindValueChanges(): void {
        this.unsubscribe();
        this.valueChangesSubscription = this.searchControl.valueChanges
            .pipe(debounceTime(200))
            .subscribe((value: string) => {
                this.filterCountryList(value);
            });
    }

    private unsubscribe(): void {
        this.valueChangesSubscription?.unsubscribe();
    }
}
